import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authApi, settingsApi } from '../../services/api';

const Setup = () => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Step 1: Encryption Key
  const [encryptionKey, setEncryptionKey] = useState('');
  const [showEncryptionKey, setShowEncryptionKey] = useState(false);
  const [currentEncryptionKey, setCurrentEncryptionKey] = useState('');
  const [isDefaultKey, setIsDefaultKey] = useState(false);
  
  // Step 2: User Creation
  const [username, setUsername] = useState('Owner');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();
  
  // Check if setup is needed
  useEffect(() => {
    const checkSetupNeeded = async () => {
      try {
        setIsLoading(true);
        
        // Try to check if setup is needed
        try {
          // This will return 200 OK if setup is needed (no users exist)
          // or throw a 403 Forbidden error if users already exist
          await authApi.checkSetupNeeded();
          
          // If we get here, setup is needed
          // Check if there's an encryption key in the environment
          try {
            // Try to get the current encryption key from the settings API
            const response = await axios.get('/api/setup/encryption-key-info');
            if (response.data && response.data.encryption_key) {
              setCurrentEncryptionKey(response.data.encryption_key);
              setIsDefaultKey(response.data.is_default);
              
              // If it's not the default key, it's coming from the environment
              if (!response.data.is_default) {
                setSuccess('Using encryption key from environment variable.');
              }
            } else {
              // Assume default encryption key
              setIsDefaultKey(true);
              setCurrentEncryptionKey('duebit-default-key');
            }
          } catch (keyErr) {
            // Assume default encryption key
            setIsDefaultKey(true);
            setCurrentEncryptionKey('duebit-default-key');
          }
        } catch (setupErr) {
          // If we get a 403 error, it means users already exist
          if (setupErr.response && setupErr.response.status === 403) {
            // Users exist, redirect to login
            navigate('/login');
          } else {
            // Some other error occurred
            setError('Failed to check if setup is needed. Please try again.');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSetupNeeded();
  }, [navigate]);
  
  // Generate a random encryption key
  const generateEncryptionKey = async () => {
    try {
      setIsLoading(true);
      // Use the setup-specific endpoint that doesn't require authentication
      const response = await axios.get('/api/setup/generate-key');
      setEncryptionKey(response.data.key);
      setSuccess('Generated a secure encryption key.');
    } catch (err) {
      setError('Failed to generate encryption key. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate a random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let result = '';
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
    setSuccess('Generated a secure password.');
  };
  
  // Copy text to clipboard
  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSuccess(message);
      })
      .catch(() => {
        setError('Failed to copy to clipboard. Please try again.');
      });
  };
  
  // Handle next step
  const handleNext = async () => {
    setError('');
    setSuccess('');
    
    if (step === 1) {
      // Validate encryption key
      if (isDefaultKey && !encryptionKey) {
        setError('Please enter an encryption key or generate one.');
        return;
      }
      
      try {
        setIsLoading(true);
        
        // During setup, we can't update the encryption key yet because we're not authenticated
        // We'll pass the encryption key to the createInitialUser API call instead
        
        // Move to next step
        setStep(2);
        
        // Generate a random password for the owner account
        generatePassword();
      } catch (err) {
        setError('Failed to proceed to next step. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else if (step === 2) {
      // Validate username and password
      if (!username) {
        setError('Please enter a username.');
        return;
      }
      
      if (!password) {
        setError('Please enter a password.');
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Create the owner account with the encryption key
        await authApi.createInitialUser({
          username,
          password,
          role: 'owner',
          encryption_key: encryptionKey || undefined
        });
        
        // Move to next step
        setStep(3);
      } catch (err) {
        setError('Failed to create user. Please try again.');
        console.error('Error creating user:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Handle finish
  const handleFinish = () => {
    navigate('/login');
  };
  
  // Render step 1: Encryption Key
  const renderStep1 = () => {
    return (
      <div>
        <h4 className="text-center mb-4">Step 1: Encryption Key</h4>
        
        {isDefaultKey ? (
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            You are currently using the default encryption key, which is not secure.
            Please enter a new encryption key or generate one.
          </div>
        ) : (
          <div className="alert alert-info" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Encryption key is set from the environment variable.</strong>
            This key will be used for encryption and cannot be changed here.
          </div>
        )}
        
        <div className="mb-3">
          <label htmlFor="encryptionKey" className="form-label">Encryption Key</label>
          <div className="input-group">
            <input
              type={showEncryptionKey ? "text" : "password"}
              className="form-control"
              id="encryptionKey"
              value={encryptionKey || currentEncryptionKey}
              onChange={(e) => setEncryptionKey(e.target.value)}
              disabled={isLoading || !isDefaultKey}
              placeholder={isDefaultKey ? "Enter a secure encryption key" : "Encryption key is set from environment"}
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setShowEncryptionKey(!showEncryptionKey)}
            >
              <i className={`fas fa-${showEncryptionKey ? 'eye-slash' : 'eye'}`}></i>
            </button>
            {(encryptionKey || currentEncryptionKey) && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => copyToClipboard(encryptionKey || currentEncryptionKey, 'Encryption key copied to clipboard.')}
              >
                <i className="fas fa-copy"></i>
              </button>
            )}
          </div>
          {isDefaultKey && (
            <div className="form-text">
              This key will be used to encrypt sensitive data. Make sure to keep it secure.
            </div>
          )}
        </div>
        
        {isDefaultKey && (
          <div className="mb-4">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={generateEncryptionKey}
              disabled={isLoading}
            >
              <i className="fas fa-key me-2"></i>
              Generate Random Key (64 chars)
            </button>
          </div>
        )}
        
        <div className="d-grid">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              <>Next <i className="fas fa-arrow-right ms-2"></i></>
            )}
          </button>
        </div>
      </div>
    );
  };
  
  // Render step 2: User Creation
  const renderStep2 = () => {
    return (
      <div>
        <h4 className="text-center mb-4">Step 2: Create Owner Account</h4>
        
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          This will be the main administrator account with full access to all features.
        </div>
        
        <div className="mb-3">
          <label htmlFor="username" className="form-label">Username</label>
          <input
            type="text"
            className="form-control"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="password" className="form-label">Password</label>
          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
            </button>
            {password && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => copyToClipboard(password, 'Password copied to clipboard.')}
              >
                <i className="fas fa-copy"></i>
              </button>
            )}
          </div>
          <div className="form-text">
            Make sure to save this password in a secure location.
          </div>
        </div>
        
        <div className="mb-4">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={generatePassword}
            disabled={isLoading}
          >
            <i className="fas fa-dice me-2"></i>
            Generate Random Password
          </button>
        </div>
        
        <div className="d-grid">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating Account...
              </>
            ) : (
              <>Next <i className="fas fa-arrow-right ms-2"></i></>
            )}
          </button>
        </div>
      </div>
    );
  };
  
  // Render step 3: Welcome
  const renderStep3 = () => {
    return (
      <div>
        <h4 className="text-center mb-4">Setup Complete!</h4>
        
        <div className="alert alert-success mb-4" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          Your application is now set up and ready to use.
        </div>
        
        <p className="mb-4">
          You can now log in with the username <strong>{username}</strong> and the password you created.
          Make sure you have saved your encryption key and password in a secure location.
        </p>
        
        <div className="d-grid">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleFinish}
          >
            Go to Login <i className="fas fa-sign-in-alt ms-2"></i>
          </button>
        </div>
      </div>
    );
  };
  
  // Render progress bar
  const renderProgressBar = () => {
    return (
      <div className="mb-4">
        <div className="progress">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${(step / 3) * 100}%` }}
            aria-valuenow={step}
            aria-valuemin="1"
            aria-valuemax="3"
          >
            Step {step} of 3
          </div>
        </div>
        <div className="d-flex justify-content-between mt-2">
          <span className={step >= 1 ? 'text-primary' : 'text-muted'}>Encryption Key</span>
          <span className={step >= 2 ? 'text-primary' : 'text-muted'}>Owner Account</span>
          <span className={step >= 3 ? 'text-primary' : 'text-muted'}>Complete</span>
        </div>
      </div>
    );
  };
  
  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <img src="/duebit-logo.png" alt="Duebit Logo" className="img-fluid" style={{ maxHeight: '100px' }} />
                <h4 className="mt-3">Setup</h4>
              </div>
              
              {renderProgressBar()}
              
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert alert-success" role="alert">
                  <i className="fas fa-check-circle me-2"></i>
                  {success}
                </div>
              )}
              
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </div>
          </div>
          
          <div className="text-center mt-3 text-muted">
            <small>
              Setup wizard for first-time configuration
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
